import { useCallback, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CTAs from "../../components/CTAs";
import Upload from "../../components/Upload";
import Footer from "../../components/Footer";
import { GalleryExport } from "iconsax-reactjs";

const DocumentUploadPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = location.state?.id;
  const token = location.state?.token || localStorage.getItem("authToken");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isConnectingXero, setIsConnectingXero] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;

  // Check for Xero callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("xero") === "connected") {
      alert("Xero connected successfully!");
    } else if (params.get("error")) {
      alert("Failed to connect to Xero");
    }
  }, []);

  const onContinueClick = useCallback(async () => {
    if (!selectedFiles.length) {
      alert("Please select files before proceeding.");
      return;
    }
    if (!userId) {
      alert("User ID is missing!");
      return;
    }

    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append("files", file);
    });
    formData.append("userId", userId);

    try {
      const response = await fetch(`${API_URL}/api/documents/invoices/upload`, {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to upload files");
      alert("Files uploaded successfully!");
      navigate("/registrationcomplete");
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  }, [selectedFiles, userId, navigate, API_URL]);

  const handleXeroConnect = async () => {
    if (!userId) {
      alert("User ID is missing!");
      return;
    }
    setIsConnectingXero(true);
    try {
      const response = await fetch(`${API_URL}/api/xero/connect`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Failed to get Xero authorization URL");
      }
    } catch (error) {
      console.error("Xero connection error:", error);
      alert(`Failed to connect to Xero: ${error.message}`);
      setIsConnectingXero(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');
        html, body { margin: 0; background: #ffffff !important; }
        .dd-root {
          font-family: 'DM Sans', sans-serif;
          background: #ffffff;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0 20px;
        }
        .dd-form-container {
          width: 400px;
          max-width: 100%;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          gap: 24px;
          margin: auto;
        }
        .dd-section-label {
          font-size: 14px; font-weight: 700; color: #0F172A;
          margin-bottom: 6px; display: block;
        }
        .dd-text { font-size: 13px; color: #64748B; }
        .dd-link {
          font-size: 13px; font-weight: 600; color: #2563EB;
          text-decoration: none; cursor: pointer;
        }
        .dd-link:hover { text-decoration: underline; }

        .dd-xero-btn {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          width: 100%; padding: 10px 18px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #0F172A;
          cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          transition: border-color 0.15s, background 0.15s;
        }
        .dd-xero-btn:hover { border-color: #94A3B8; background: #F8FAFC; }
        .dd-xero-btn img { width: 24px; height: 24px; }
      `}</style>

      <div className="dd-root">
        <div className="dd-form-container">
          {/* Icon & Heading */}
          <div style={{ textAlign: "center", marginBottom: "8px" }}>
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "8px",
                background: "#F8FAFC",
                border: "1px solid #E2E8F0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <GalleryExport size={20} />
            </div>
            <h1
              style={{
                fontSize: "24px",
                fontWeight: 700,
                color: "#0F172A",
                margin: "0 0 8px 0",
              }}
            >
              Upload your documents
            </h1>
            <p className="dd-text">Register to your account</p>
          </div>

          {/* Upload component */}
          <Upload width="100%" onFilesSelected={setSelectedFiles} />

          {/* Buttons */}
          <CTAs
            type="Signinbtn"
            text="Continue"
            width="100%"
            onClick={onContinueClick}
          />

          {/* Xero button */}
          <button
            className="dd-xero-btn"
            onClick={handleXeroConnect}
            disabled={isConnectingXero}
          >
            <img src="assets/xero.svg" alt="Xero" />
            {isConnectingXero ? "Connecting..." : "Sign in with Xero"}
          </button>

          <div style={{ textAlign: "center" }}>
            <span className="dd-text">Already have an account? </span>
            <span className="dd-link" onClick={() => navigate("/login")}>
              Log in
            </span>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default DocumentUploadPage;
